/**
 * Custom window resize utility that only triggers when browser width changes
 * Prevents unnecessary triggers from iOS scroll events and other non-width changes
 */

class CustomResize {
  private windowWidth: number;
  private callbacks: Set<(width: number) => void>;

  constructor() {
    this.windowWidth = window.innerWidth;
    this.callbacks = new Set();
    this.init();
  }

  private init(): void {
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  private handleResize(): void {
    // Check if window width has actually changed
    if (window.innerWidth !== this.windowWidth) {
      // Update the window width for next time
      this.windowWidth = window.innerWidth;

      // Execute all registered callbacks
      this.callbacks.forEach(callback => {
        callback(this.windowWidth);
      });
    }
  }

  /**
   * Add a callback function to be executed when window width changes
   * @param callback - Function to execute when width changes, receives new width as parameter
   * @returns Function to remove the callback
   */
  onWidthChange(callback: (width: number) => void): () => void {
    this.callbacks.add(callback);

    // Return a function to remove this callback
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Remove a specific callback
   * @param callback - The callback function to remove
   */
  removeCallback(callback: (width: number) => void): void {
    this.callbacks.delete(callback);
  }

  /**
   * Remove all callbacks
   */
  removeAllCallbacks(): void {
    this.callbacks.clear();
  }

  /**
   * Get current window width
   */
  getCurrentWidth(): number {
    return this.windowWidth;
  }

  /**
   * Destroy the resize listener and clean up
   */
  destroy(): void {
    window.removeEventListener("resize", this.handleResize.bind(this));
    this.callbacks.clear();
  }
}

// Create a singleton instance
const customResize = new CustomResize();

// Export both the class and the singleton instance
export { CustomResize, customResize };

// Default export is the singleton instance
export default customResize;
